// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * ARCHAT - Sistema de Logging Frontend
 * 
 * @file src/utils/logger.js
 * @description Sistema de logging centralizado para React
 * @version 2.0.0
 * 
 * CARACTERÍSTICAS:
 * - Logging condicional por entorno (development/production)
 * - Intercepta errores globales de React
 * - Sanitización automática de datos sensibles
 * - Opción de enviar logs al backend
 * 
 * USO:
 * import { logger } from '../utils/logger';
 * logger.info('Mensaje', { context: 'data' });
 * logger.error('Error', { error: err });
 * logger.debug('Debug info', { variable: value });
 * 
 * CONFIGURACIÓN:
 * - VITE_ARCHAT_ENV: development | production
 * - VITE_LOG_LEVEL: debug | info | error | off
 */

import { request as apiClientRequest } from '../services/apiClient';
import { API_BASE } from './constants.js';

// ============================================================
// CONFIGURACIÓN
// ============================================================

const ENV = import.meta.env.VITE_ARCHAT_ENV || import.meta.env.MODE || 'production';
const LOG_LEVEL = import.meta.env.VITE_LOG_LEVEL || (ENV === 'development' ? 'debug' : 'error');

// Niveles de log (orden de importancia)
const LEVELS = {
  off: 0,
  error: 1,
  info: 2,
  debug: 3
};

const apiBase = (API_BASE && typeof API_BASE === 'string') ? API_BASE.replace(/\/$/, '') : (import.meta.env.VITE_API_BASE || '/api');

// Configuración de logging
const config = {
  environment: ENV,
  logLevel: LOG_LEVEL,
  enableConsole: ENV === 'development' || LOG_LEVEL !== 'off',
  enableBackend: ENV === 'development' && import.meta.env.VITE_ENABLE_LOG_BACKEND === 'true',
  backendEndpoint: apiBase + '/log.php'
};

// ============================================================
// UTILIDADES
// ============================================================

/**
 * Verificar si se debe registrar un nivel de log
 * @param {string} level Nivel a verificar
 * @returns {boolean} True si se debe registrar
 */
function shouldLog(level) {
  if (config.logLevel === 'off') {
    return false;
  }
  
  const requestedLevel = LEVELS[level] || 0;
  const configuredLevel = LEVELS[config.logLevel] || 0;
  
  return requestedLevel <= configuredLevel;
}

/**
 * Sanitizar contexto para eliminar datos sensibles
 * @param {Object} context Contexto original
 * @returns {Object} Contexto sanitizado
 */
function sanitizeContext(context) {
  if (!context || typeof context !== 'object') {
    return {};
  }
  
  const blockedKeys = [
    'password',
    'passwordHash',
    'password_confirm',
    'token',
    'accessToken',
    'refreshToken',
    'apiKey',
    'secret',
    'authorization',
    'Authorization',
    'csrfToken',
    'sessionId'
  ];
  
  const sanitized = {};
  
  for (const [key, value] of Object.entries(context)) {
    const keyLower = key.toLowerCase();
    
    // Saltar claves bloqueadas
    if (blockedKeys.some(blocked => keyLower.includes(blocked.toLowerCase()))) {
      sanitized[key] = '[REDACTED]';
      continue;
    }
    
    // Enmascarar emails
    if (keyLower === 'email' && typeof value === 'string') {
      const email = value.trim().toLowerCase();
      if (email.includes('@')) {
        const [user, domain] = email.split('@');
        sanitized[key] = `${user[0]}***${user.slice(-1)}@${domain[0]}***`;
      } else {
        sanitized[key] = value;
      }
      continue;
    }
    
    // Limitar tamaño de strings largos
    if (typeof value === 'string' && value.length > 500) {
      sanitized[key] = value.substring(0, 500) + '...[truncated]';
      continue;
    }
    
    // Limitar arrays grandes
    if (Array.isArray(value) && value.length > 50) {
      sanitized[key] = value.slice(0, 50).concat(`[${value.length - 50} more items]`);
      continue;
    }
    
    sanitized[key] = value;
  }
  
  return sanitized;
}

/**
 * Obtener información del stack trace
 * @returns {Object} Información del caller
 */
function getCallerInfo() {
  try {
    const stack = new Error().stack;
    if (!stack) {
      return { file: 'unknown', line: 0, function: 'unknown' };
    }
    
    const lines = stack.split('\n');
    // Buscar la primera línea que no sea de logger.js
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (line && !line.includes('logger.js') && !line.includes('Error')) {
        const match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)/) || 
                     line.match(/at\s+(.+?):(\d+):(\d+)/);
        if (match) {
          return {
            file: match[2] ? match[2].split('/').pop() : 'unknown',
            line: parseInt(match[3] || match[2]) || 0,
            function: match[1] || 'unknown'
          };
        }
      }
    }
  } catch (e) {
    // Ignorar errores al obtener caller info
  }
  
  return { file: 'unknown', line: 0, function: 'unknown' };
}

/**
 * Enviar log al backend (opcional, solo en development)
 * @param {string} level Nivel de log
 * @param {string} message Mensaje
 * @param {Object} context Contexto
 */
async function sendToBackend(level, message, context) {
  if (!config.enableBackend) {
    return;
  }
  
  try {
    const caller = getCallerInfo();
    const logEntry = {
      timestamp: new Date().toISOString(),
      level: level.toUpperCase(),
      environment: config.environment,
      user_id: context.userId || context.user_id || null,
      tenant_id: context.tenantId || context.tenant_id || null,
      file: caller.file,
      line: caller.line,
      function: caller.function,
      message,
      context: sanitizeContext(context),
      user_agent: navigator.userAgent,
      url: window.location.href
    };
    
    // Enviar de forma asíncrona (no bloquear) — cliente centralizado (Bearer + credentials)
    apiClientRequest(config.backendEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(logEntry)
    }).catch(() => {
      // Ignorar errores de envío (no crítico)
    });
  } catch (e) {
    // Ignorar errores de envío
  }
}

// ============================================================
// LOGGER PRINCIPAL
// ============================================================

const logger = {
  /**
   * Log de información
   * @param {string} message Mensaje
   * @param {Object} context Contexto adicional
   */
  info(message, context = {}) {
    if (!shouldLog('info')) return;
    
    const caller = getCallerInfo();
    const sanitized = sanitizeContext(context);
    
    if (config.enableConsole) {
      console.log(`[INFO] ${message}`, {
        ...sanitized,
        _meta: { file: caller.file, line: caller.line }
      });
    }
    
    sendToBackend('info', message, context);
  },
  
  /**
   * Log de error
   * @param {string} message Mensaje de error
   * @param {Object} context Contexto adicional
   */
  error(message, context = {}) {
    if (!shouldLog('error')) return;
    
    const caller = getCallerInfo();
    const sanitized = sanitizeContext(context);
    
    if (config.enableConsole) {
      console.error(`[ERROR] ${message}`, {
        ...sanitized,
        _meta: { file: caller.file, line: caller.line }
      });
      
      // Si hay un error object, mostrar stack trace
      if (context.error && context.error instanceof Error) {
        console.error('Stack trace:', context.error.stack);
      }
    }
    
    sendToBackend('error', message, context);
  },
  
  /**
   * Log de advertencia
   * @param {string} message Mensaje de advertencia
   * @param {Object} context Contexto adicional
   */
  warning(message, context = {}) {
    if (!shouldLog('info')) return; // Warnings se registran como info
    
    const caller = getCallerInfo();
    const sanitized = sanitizeContext(context);
    
    if (config.enableConsole) {
      console.warn(`[WARNING] ${message}`, {
        ...sanitized,
        _meta: { file: caller.file, line: caller.line }
      });
    }
    
    sendToBackend('warning', message, context);
  },
  
  /**
   * Log de depuración
   * @param {string} message Mensaje de debug
   * @param {Object} context Contexto adicional
   */
  debug(message, context = {}) {
    if (!shouldLog('debug')) return;
    
    const caller = getCallerInfo();
    const sanitized = sanitizeContext(context);
    
    if (config.enableConsole) {
      console.debug(`[DEBUG] ${message}`, {
        ...sanitized,
        _meta: { file: caller.file, line: caller.line }
      });
    }
    
    sendToBackend('debug', message, context);
  },
  
  /**
   * Obtener configuración actual
   * @returns {Object} Configuración
   */
  getConfig() {
    return { ...config };
  }
};

// ============================================================
// INTERCEPTORES GLOBALES DE ERRORES
// ============================================================

if (config.environment === 'development') {
  // Interceptar errores no capturados
  window.addEventListener('error', (event) => {
    logger.error('Uncaught error', {
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error
    });
  });
  
  // Interceptar promesas rechazadas
  window.addEventListener('unhandledrejection', (event) => {
    logger.error('Unhandled promise rejection', {
      reason: event.reason,
      error: event.reason instanceof Error ? event.reason : null
    });
  });
}

// ============================================================
// EXPORT
// ============================================================

export { logger };
export default logger;
